// src/components/WebSocketService.js
// UPDATED: Added call signaling event types for voice/video calls
// New events: call_invite, call_accepted, call_rejected, call_busy,
//             call_ended, call_participant_joined, call_participant_left
//
// UPDATED: Added resource_added / resource_updated / resource_deleted
// event types — broadcast by ResourceWebSocketHandler on the backend
// whenever a resource is created/edited/deleted, so the Resources page
// and its unread-count badge can refetch instantly across every other
// connected browser instead of waiting on a polling interval.

class WebSocketService {
  constructor() {
    this.ws                   = null;
    this.reconnectAttempts    = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval    = 5000;
    this.reconnectTimer       = null;
    this.isConnecting         = false;
    this.listeners            = new Map();
    this.messageQueue         = [];
    this.userId               = null;
    this.connectionId         = null;
    this.connectionTimeout    = null;
    this.token                = null;
    this._consumerCount       = 0;
    this.wsUrl                = this._buildWsUrl();
    this.heartbeatInterval    = 30000;
    this.heartbeatTimer       = null;
    this.lastHeartbeat        = null;
    this.heartbeatEnabled     = false;
  }

  acquire() {
    this._consumerCount++;
    console.log(`WebSocket: acquire() → consumers=${this._consumerCount}`);
  }

  release(force = false) {
    this._consumerCount = Math.max(0, this._consumerCount - 1);
    console.log(`WebSocket: release() → consumers=${this._consumerCount}`);
    if (force || this._consumerCount === 0) this._realDisconnect();
  }

  _buildWsUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const hostname  = window.location.hostname;
    if (hostname === 'api.eilandigital.com' || process.env.NODE_ENV === 'production') {
      return 'wss://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/ws';
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'ws://localhost:8080/ws';
    }
    return `${protocol}//${hostname}:8080/ws`;
  }

  async connect(userId, token = null) {
    if (this.isConnecting) return Promise.resolve();
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return Promise.resolve();
    if (!userId) throw new Error('userId is required');
    this.userId       = userId;
    this.token        = token;
    this.isConnecting = true;
    return new Promise((resolve, reject) => {
      try {
        let url = `${this.wsUrl}?userId=${encodeURIComponent(userId)}`;
        if (token && typeof token === 'string') url += `&token=${encodeURIComponent(token)}`;
        console.log('WebSocket: connecting to', this.wsUrl, 'userId:', userId);
        this.ws = new WebSocket(url);
        this.connectionTimeout = setTimeout(() => {
          if (this.ws?.readyState === WebSocket.CONNECTING) {
            console.error('WebSocket: connection timeout');
            this.ws.close();
            this.isConnecting = false;
            reject(new Error('Connection timeout'));
          }
        }, 10000);
        this.ws.onopen = () => {
          this._clearConnectionTimeout();
          console.log('WebSocket: connected');
          this.isConnecting      = false;
          this.reconnectAttempts = 0;
          this._processMessageQueue();
          this.emit('connected', { userId, connectionId: this.connectionId });
          resolve();
        };
        this.ws.onmessage = (event) => {
          try { this._handleMessage(event.data); }
          catch (err) {
            console.error('WebSocket: message handler error:', err);
            this.emit('parseError', { error: err.message, rawData: event.data, timestamp: Date.now() });
          }
        };
        this.ws.onclose = (event) => {
          this._clearConnectionTimeout();
          console.log(`WebSocket: closed code=${event.code} reason="${event.reason}" clean=${event.wasClean}`);
          this.isConnecting = false;
          this._stopHeartbeat();
          this.emit('disconnected', { code: event.code, reason: event.reason, wasClean: event.wasClean });
          this._handleConnectionClosure(event);
        };
        this.ws.onerror = () => {
          this._clearConnectionTimeout();
          this.isConnecting = false;
          this._handleConnectionError();
          reject(new Error('WebSocket connection error'));
        };
      } catch (err) {
        this.isConnecting = false;
        reject(err);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MESSAGE HANDLER
  // Handles both camelCase (backend sends) and snake_case (legacy).
  // UPDATED: Added all call signaling event types.
  // UPDATED: Added resource_added / resource_updated / resource_deleted.
  // ─────────────────────────────────────────────────────────────────────────
  _handleMessage(data) {
    const message = JSON.parse(data);
    console.log('WebSocket: received', message.type);

    switch (message.type) {
      case 'connection_established':
      case 'connected':
        this.connectionId     = message.connectionId || message.data?.connectionId;
        this.heartbeatEnabled = true;
        this._startHeartbeat();
        break;

      case 'heartbeat':
      case 'ping':
        this.lastHeartbeat = Date.now();
        if (message.requiresResponse) this.send('pong', { timestamp: Date.now() });
        break;

      case 'error': {
        const msg = message.error || message.data?.error || message.message || 'Unknown server error';
        console.error('WebSocket: server error:', msg);
        this.emit('serverError', { message: msg, code: message.code || 'SERVER_ERROR', timestamp: Date.now() });
        break;
      }

      case 'send_message':
        console.warn('send_message via WebSocket is disabled — use HTTP API');
        return;

      // ── project chat ─────────────────────────────────────────────────────
      case 'chatNewMessage':
      case 'chat_new_message':
        this.emit('chatNewMessage', message.data || message);
        break;

      case 'chatMessageEdited':
      case 'chat_message_edited':
        this.emit('chatMessageEdited', message.data || message);
        break;

      case 'chatMessageDeleted':
      case 'chat_message_deleted':
        this.emit('chatMessageDeleted', message.data || message);
        break;

      case 'chatParticipantRemoved':
      case 'chat_participant_removed':
        this.emit('chatParticipantRemoved', message.data || message);
        break;

      // ── resources (NEW) ──────────────────────────────────────────────────
      // Broadcast by ResourceWebSocketHandler to every connected user
      // (except the actor, who already updated their own UI optimistically)
      // whenever a resource is created, edited, or soft-deleted.
      case 'resource_added':
        this.emit('resourceAdded', message.data || message);
        break;

      case 'resource_updated':
        this.emit('resourceUpdated', message.data || message);
        break;

      case 'resource_deleted':
        this.emit('resourceDeleted', message.data || message);
        break;

      case 'category_added':
        this.emit('categoryAdded', message.data || message);
        break;

      // ── notifications ─────────────────────────────────────────────────────
      case 'notification':
        this.emit('notification', message.data);
        break;

      // ── direct chat ───────────────────────────────────────────────────────
      case 'new_message':         this.emit('newMessage',        message.data); break;
      case 'message_updated':     this.emit('messageUpdated',    message.data); break;
      case 'message_deleted':     this.emit('messageDeleted',    message.data); break;
      case 'messageDelivered':    this.emit('messageDelivered',  message.data); break;
      case 'user_typing':         this.emit('userTyping',        message.data); break;
      case 'user_stopped_typing': this.emit('userStoppedTyping', message.data); break;
      case 'user_online':         this.emit('userOnline',        message.data); break;
      case 'user_offline':        this.emit('userOffline',       message.data); break;
      case 'chat_created':        this.emit('chatCreated',       message.data); break;
      case 'user_joined_chat':    this.emit('userJoinedChat',    message.data); break;
      case 'user_left_chat':      this.emit('userLeftChat',      message.data); break;
      case 'message_read':        this.emit('message_read',      message.data); break;
      case 'success':
      case 'ack':                 this.emit('serverAck', message.data || message); break;

      // ── CALL SIGNALING ────────────────────────────────────────────────────
      // These events are relayed by ChatWebSocketHandler between users.
      // The server does not interpret the payloads, just forwards them.

      // Incoming call invitation (individual or group)
      case 'call_invite':
        this.emit('callInvite', message.data || message);
        break;

      // Recipient accepted the call
      case 'call_accepted':
        this.emit('callAccepted', message.data || message);
        break;

      // Recipient rejected the call
      case 'call_rejected':
        this.emit('callRejected', message.data || message);
        break;

      // Recipient is already on another call
      case 'call_busy':
        this.emit('callBusy', message.data || message);
        break;

      // Call ended by any participant
      case 'call_ended':
        this.emit('callEnded', message.data || message);
        break;

      // A new participant joined the group call
      case 'call_participant_joined':
        this.emit('callParticipantJoined', message.data || message);
        break;

      // A participant left the group call
      case 'call_participant_left':
        this.emit('callParticipantLeft', message.data || message);
        break;

      default:
        console.log('WebSocket: unknown message type:', message.type);
        this.emit('unknownMessage', message);
    }
  }

  _handleConnectionClosure(event) {
    switch (event.code) {
      case 1000:
      case 1001:
        break;
      case 1008:
        this.emit('authenticationError', { code: event.code, reason: event.reason });
        break;
      case 1011:
        this.emit('serverError', { message: 'Server rejected (1011)', code: event.code, timestamp: Date.now() });
        if (this.reconnectAttempts < 2) setTimeout(() => this._scheduleReconnect(), 10000);
        else this.emit('maxReconnectAttemptsReached', { attempts: this.reconnectAttempts });
        break;
      default:
        if (this._consumerCount > 0) this._scheduleReconnect();
    }
  }

  _handleConnectionError() {
    this.emit('connectionError', { message: 'WebSocket connection failed', type: 'CONNECTION_ERROR', timestamp: Date.now(), reconnectAttempts: this.reconnectAttempts });
    if (this._consumerCount > 0 && this.reconnectAttempts < this.maxReconnectAttempts) this._scheduleReconnect();
  }

  send(type, data = {}) {
    if (!type) return false;
    if (!this.isConnected()) { this._queueMessage({ type, data }); return false; }
    const payload = { type, data: { ...data, userId: this.userId, timestamp: Date.now() } };
    try { this.ws.send(JSON.stringify(payload)); return true; }
    catch (err) { this._queueMessage(payload); this.emit('sendError', { error: err.message, message: payload, timestamp: Date.now() }); return false; }
  }

  // ── Call signaling helpers ───────────────────────────────────────────────
  // These never queue — stale call signals are worse than dropped ones.

  sendCallInvite(targetUserId, payload) {
    if (!this.isConnected()) return false;
    return this.send('call_invite', { targetUserId, ...payload });
  }

  sendCallAccepted(targetUserId, payload) {
    if (!this.isConnected()) return false;
    return this.send('call_accepted', { targetUserId, ...payload });
  }

  sendCallRejected(targetUserId, payload) {
    if (!this.isConnected()) return false;
    return this.send('call_rejected', { targetUserId, ...payload });
  }

  sendCallBusy(targetUserId) {
    if (!this.isConnected()) return false;
    return this.send('call_busy', { targetUserId });
  }

  sendCallEnded(targetUserIds, payload) {
    if (!this.isConnected()) return false;
    // targetUserIds can be a single id or an array
    const ids = Array.isArray(targetUserIds) ? targetUserIds : [targetUserIds];
    ids.forEach(id => this.send('call_ended', { targetUserId: id, ...payload }));
    return true;
  }

  sendCallParticipantJoined(targetUserIds, payload) {
    if (!this.isConnected()) return false;
    const ids = Array.isArray(targetUserIds) ? targetUserIds : [targetUserIds];
    ids.forEach(id => this.send('call_participant_joined', { targetUserId: id, ...payload }));
    return true;
  }

  sendCallParticipantLeft(targetUserIds, payload) {
    if (!this.isConnected()) return false;
    const ids = Array.isArray(targetUserIds) ? targetUserIds : [targetUserIds];
    ids.forEach(id => this.send('call_participant_left', { targetUserId: id, ...payload }));
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────────

  _queueMessage(message) {
    // Never queue call signaling messages — they are time-sensitive
    const callTypes = [
      'call_invite', 'call_accepted', 'call_rejected',
      'call_busy', 'call_ended', 'call_participant_joined', 'call_participant_left',
    ];
    if (callTypes.includes(message.type)) return;

    this.messageQueue.push({ ...message, queuedAt: Date.now() });
    if (this.messageQueue.length > 50) this.messageQueue.splice(0, this.messageQueue.length - 50);
  }

  _processMessageQueue() {
    if (!this.messageQueue.length) return;
    const queue = [...this.messageQueue];
    this.messageQueue = [];
    queue.forEach((msg, i) => setTimeout(() => {
      if (this.isConnected()) { try { this.ws.send(JSON.stringify(msg)); } catch { this._queueMessage(msg); } }
      else { this._queueMessage(msg); }
    }, i * 200));
  }

  _startHeartbeat() {
    if (!this.heartbeatEnabled) return;
    this._stopHeartbeat();
    this.lastHeartbeat  = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected() && this.heartbeatEnabled) this.send('heartbeat', { timestamp: Date.now() });
      else this._stopHeartbeat();
    }, this.heartbeatInterval);
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000);
    console.log(`WebSocket: reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      if (this.reconnectAttempts <= this.maxReconnectAttempts && this.userId) {
        try { await this.connect(this.userId, this.token); }
        catch (err) {
          if (this.reconnectAttempts < this.maxReconnectAttempts && this._consumerCount > 0) this._scheduleReconnect();
          else this.emit('maxReconnectAttemptsReached', { attempts: this.reconnectAttempts, lastError: err.message });
        }
      }
    }, delay);
  }

  _clearReconnectTimer()    { if (this.reconnectTimer)    { clearTimeout(this.reconnectTimer);    this.reconnectTimer    = null; } }
  _clearConnectionTimeout() { if (this.connectionTimeout) { clearTimeout(this.connectionTimeout); this.connectionTimeout = null; } }

  _realDisconnect() {
    console.log('WebSocket: _realDisconnect()');
    this._stopHeartbeat();
    this._clearReconnectTimer();
    this._clearConnectionTimeout();
    this.heartbeatEnabled = false;
    if (this.ws) {
      const ws = this.ws; this.ws = null;
      try { if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close(1000, 'All consumers released'); }
      catch (e) { console.error('WebSocket: close error:', e); }
    }
    this.reconnectAttempts = 0;
    this.connectionId      = null;
    this.isConnecting      = false;
  }

  disconnect(force = false) {
    if (force) { this._consumerCount = 0; this._realDisconnect(); }
    else console.warn('WebSocket: disconnect() called without force — use release() instead');
  }

  async reconnect() {
    if (this.isConnecting) return;
    this._realDisconnect();
    this.reconnectAttempts = 0;
    await new Promise(r => setTimeout(r, 500));
    if (this.userId) return this.connect(this.userId, this.token);
    throw new Error('No userId available for reconnection');
  }

  destroy() {
    this._consumerCount = 0;
    this._realDisconnect();
    this.listeners.clear();
    this.messageQueue = [];
    this.userId       = null;
    this.connectionId = null;
    this.token        = null;
  }

  sendChatMessage(chatId, messageText, messageType = 'text', replyToId = null, attachments = null) {
    return this.send('send_message', { chatId, messageText, messageType, replyToMessageId: replyToId, attachments });
  }
  markAsRead(chatId, messageId)              { return this.send('mark_as_read',           { chatId, messageId }); }
  sendTypingIndicator(chatId, isTyping)      { return this.send(isTyping ? 'user_typing' : 'user_stopped_typing', { chatId }); }
  joinChat(chatId)                           { return this.send('join_chat',               { chatId }); }
  leaveChat(chatId)                          { return this.send('leave_chat',              { chatId }); }
  updatePresence(status, statusMessage=null) { return this.send('update_presence',         { status, statusMessage }); }

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, []);
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const arr = this.listeners.get(event);
    const idx = arr.indexOf(callback);
    if (idx > -1) arr.splice(idx, 1);
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    this.listeners.get(event).forEach(cb => {
      try { cb(data); } catch (err) { console.error(`WebSocket: listener error for "${event}":`, err); }
    });
  }

  isConnected() { return this.ws?.readyState === WebSocket.OPEN; }

  getConnectionStatus() {
    if (!this.ws) return 'disconnected';
    return ['connecting','connected','disconnecting','disconnected'][this.ws.readyState] ?? 'unknown';
  }

  getConnectionInfo() {
    return {
      status: this.getConnectionStatus(), userId: this.userId, connectionId: this.connectionId,
      reconnectAttempts: this.reconnectAttempts, maxReconnectAttempts: this.maxReconnectAttempts,
      queuedMessages: this.messageQueue.length, lastHeartbeat: this.lastHeartbeat,
      heartbeatEnabled: this.heartbeatEnabled, consumerCount: this._consumerCount, wsUrl: this.wsUrl,
    };
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;